import React from "react";
import { motion } from "framer-motion";

const BADGES = [
  {
    src: "/images/badges/risk-learning.png",
    title: "Certified faculty",
    subtitle: "Risk Learning",
    issuer: "Risk.net",
  },
  {
    src: "/images/badges/bpg-cid.png",
    title: "Certified instructional designer",
    subtitle: "Adult learning & curriculum design",
    issuer: "The Bob Pike Group",
  },
];

export const Accreditations = () => {
  return (
    <section
      id="accreditations"
      data-testid="accreditations-section"
      className="relative py-16 md:py-24 bg-[#F9F8F6] overflow-hidden"
    >
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div className="absolute left-0 right-0 top-16 h-px bg-[#0D0D0D]/5" />
        <div className="absolute left-0 right-0 bottom-16 h-px bg-[#0D0D0D]/5" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-center">
          {/* Intro text */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5"
          >
            <p className="font-sans-body text-[11px] tracking-widest-2 uppercase text-[#3E7373] mb-6 flex items-center gap-3">
              <span className="inline-block w-10 h-px bg-[#3E7373]" />
              Credentials
            </p>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-[56px] leading-[1.05] tracking-tight text-[#0D0D0D] font-medium">
              Credentials where
              <br />
              <span className="italic text-[#3E7373]">it matters most.</span>
            </h2>
            <p className="font-sans-body text-[15px] md:text-base leading-relaxed text-[#4A4A4A] mt-6 max-w-md">
              Independent recognition from the institutions that set the standard for risk education and adult learning.
            </p>
          </motion.div>

          {/* Badges */}
          {BADGES.map((badge, idx) => (
            <motion.article
              key={badge.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.9, delay: 0.12 + idx * 0.1, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`accreditation-${idx}`}
              className="group relative lg:col-span-3 lg:col-start-auto flex flex-col items-center text-center"
            >
              <span className="font-serif-display italic text-[#3E7373]/60 text-xs tracking-widest-2 mb-4">
                — {String(idx + 1).padStart(2, "0")} —
              </span>

              <div className="relative mb-7">
                <div className="absolute inset-0 -m-3 rounded-full bg-white shadow-[0_24px_60px_-32px_rgba(13,13,13,0.18)] transition-transform duration-700 group-hover:scale-[1.04]" aria-hidden="true" />
                <div className="absolute inset-0 -m-1 rounded-full border border-[#9CDBD9]/60" aria-hidden="true" />
                <div className="relative w-36 h-36 md:w-40 md:h-40 rounded-full bg-white flex items-center justify-center p-6 border border-[#0D0D0D]/10">
                  <img
                    src={badge.src}
                    alt={`${badge.title} — ${badge.issuer}`}
                    loading="lazy"
                    decoding="async"
                    width="160"
                    height="160"
                    sizes="(max-width: 768px) 33vw, 160px"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>

              <p className="font-sans-body text-[10px] tracking-widest-2 uppercase text-[#3E7373] mb-3">
                Issued by <span className="text-[#0D0D0D]">{badge.issuer}</span>
              </p>
              <h3 className="font-serif-display text-xl md:text-[20px] leading-[1.2] text-[#0D0D0D] mb-1">
                {badge.title}
              </h3>
              <p className="font-serif-display italic text-[#3E7373] text-sm md:text-base">
                {badge.subtitle}
              </p>
            </motion.article>
          ))}

          {/* Right edge ornament filler to keep grid balanced on lg (5 + 3 + 3 = 11 cols, leaving 1 col gutter) */}
          <div aria-hidden="true" className="hidden lg:block lg:col-span-1" />
        </div>
      </div>
    </section>
  );
};

export default Accreditations;
